import { useMemo, useState } from 'react';
import { Badge, Button, Card, Checkbox, Group, Stack, Text, Title } from '@mantine/core';
import { IconDeviceFloppy } from '@tabler/icons-react';
import type { ShipmentDetail, ShipmentUnitsPatchInput } from '@expeditor/shared';
import {
  CargoGroupEditor,
  PositionsEditor,
  emptyCargoGroup,
  makeUnit,
  unitLabel,
  unitsWord,
  type CargoGroupForm,
  type TransportUnitForm,
} from '../../shared/ui/transport-units';
import { shipmentsApi } from '../../shared/api/shipments';

/**
 * Раздел «Контейнеры» в карточке перевозки.
 *
 * Контейнеры/вагоны живут на поручении; здесь staff редактирует их уже на этапе
 * перевозки — добавляет, присваивает/правит номера и распределяет груз. Это
 * нормальный процесс: номера присваиваются в пути. Клиент видит только просмотр.
 */
export function ShipmentUnitsSection({
  shipment,
  canEdit,
  onSaved,
}: {
  shipment: ShipmentDetail;
  canEdit: boolean;
  onSaved: (next: ShipmentDetail) => void;
}) {
  const initialUnits: TransportUnitForm[] = useMemo(
    () =>
      (shipment.transportUnits ?? []).map((u) => ({
        id: u.id ?? makeUnit().id,
        kind: (u.kind ?? 'CONTAINER') as TransportUnitForm['kind'],
        type: u.type,
        number: u.number ?? '',
        ownership: (u.ownership ?? '') as TransportUnitForm['ownership'],
      })),
    [shipment.transportUnits],
  );

  const initialGroups: CargoGroupForm[] = useMemo(
    () =>
      (shipment.cargoGroups ?? []).map((g) => ({
        ...emptyCargoGroup(),
        unitIds: g.unitIds ?? [],
        cargoName: g.cargoName ?? '',
        cargoWeightKg: g.cargoWeightKg ?? '',
        cargoWeightNetKg: g.cargoWeightNetKg ?? '',
        cargoVolumeM3: g.cargoVolumeM3 ?? '',
        cargoUnits: g.cargoUnits ?? '',
        cargoPackaging: g.cargoPackaging ?? '',
        cargoTnVed: g.cargoTnVed ?? '',
        cargoMarking: g.cargoMarking ?? '',
        cargoIsDangerous: g.cargoIsDangerous ?? false,
        cargoAdrClass: g.cargoAdrClass ?? '',
      })),
    [shipment.cargoGroups],
  );

  const [units, setUnits] = useState<TransportUnitForm[]>(initialUnits);
  const [sameCargoForAll, setSameCargoForAll] = useState(shipment.sameCargoForAll ?? true);
  const [cargoGroups, setCargoGroups] = useState<CargoGroupForm[]>(initialGroups);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState(false);

  // Секция нужна, только если единицы транспорта есть (или их можно завести:
  // тип отправки поручения так или иначе о них говорит).
  const hasWagons = units.some((u) => u.kind === 'WAGON');
  const hasContainers = units.some((u) => u.kind === 'CONTAINER');
  const isUnitized = Boolean(shipment.shipmentType) || units.length > 0;
  if (!isUnitized) return null;

  const sectionTitle =
    hasWagons && hasContainers
      ? 'Единицы транспорта'
      : hasWagons
        ? 'Вагоны'
        : 'Контейнеры';
  const unitWordPlural = unitsWord(units).inMany;
  const showGroups = units.length > 1;
  const num = (v: number | string) => (v === '' ? undefined : Number(v));

  const toggleSameCargo = (checked: boolean) => {
    setSameCargoForAll(checked);
    if (!checked && cargoGroups.length === 0) {
      setCargoGroups(units.map((u) => ({ ...emptyCargoGroup(), unitIds: [u.id] })));
    }
  };

  const save = async () => {
    const multiCargo = showGroups && !sameCargoForAll;
    const dto: ShipmentUnitsPatchInput = {
      transportUnits: units.map((u) => ({
        id: u.id,
        kind: u.kind,
        type: u.type,
        number: u.number.trim() || undefined,
        ownership: u.ownership || undefined,
      })),
      sameCargoForAll: !multiCargo,
      cargoGroups: multiCargo
        ? cargoGroups.map((g) => ({
            unitIds: g.unitIds,
            cargoName: g.cargoName,
            cargoWeightKg: num(g.cargoWeightKg),
            cargoWeightNetKg: num(g.cargoWeightNetKg),
            cargoVolumeM3: num(g.cargoVolumeM3),
            cargoUnits: num(g.cargoUnits),
            cargoPackaging: g.cargoPackaging || undefined,
            cargoTnVed: g.cargoTnVed || undefined,
            cargoMarking: g.cargoMarking || undefined,
            cargoIsDangerous: g.cargoIsDangerous,
            cargoAdrClass: g.cargoAdrClass || undefined,
          }))
        : undefined,
    };
    setBusy(true);
    setError(null);
    try {
      const next = await shipmentsApi.updateUnits(shipment.id, dto);
      onSaved(next);
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  const withNumbers = units.filter((u) => u.number.trim()).length;

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Title order={4}>{sectionTitle}</Title>
          {units.length > 0 && (
            <Badge variant="light">
              {units.length} ед · {withNumbers} с номерами
            </Badge>
          )}
        </Group>
        {canEdit && (
          <Button
            size="xs"
            leftSection={<IconDeviceFloppy size={14} />}
            onClick={save}
            loading={busy}
          >
            Сохранить
          </Button>
        )}
      </Group>

      {error && (
        <Text c="red" size="sm" mb="xs">
          {error}
        </Text>
      )}
      {savedMsg && (
        <Text c="teal" size="sm" mb="xs">
          Сохранено.
        </Text>
      )}

      {canEdit ? (
        <Stack>
          <PositionsEditor value={units} onChange={setUnits} />
          {showGroups && (
            <Checkbox
              label={`Во всех ${unitWordPlural} один груз`}
              description={`Снимите, чтобы распределить разный груз по ${unitWordPlural}`}
              checked={sameCargoForAll}
              onChange={(e) => toggleSameCargo(e.currentTarget.checked)}
            />
          )}
          {showGroups && !sameCargoForAll && (
            <CargoGroupEditor
              units={units}
              value={cargoGroups}
              onChange={setCargoGroups}
              kindLabelPlural={unitWordPlural}
            />
          )}
        </Stack>
      ) : (
        <ReadOnlyUnits units={units} emptyLabel="Единицы транспорта не указаны." />
      )}
    </Card>
  );
}

/** Просмотр единиц для клиента — без редактирования. */
function ReadOnlyUnits({ units, emptyLabel }: { units: TransportUnitForm[]; emptyLabel: string }) {
  if (units.length === 0) return <Text c="dimmed" size="sm">{emptyLabel}</Text>;
  return (
    <Stack gap={4}>
      {units.map((u, i) => (
        <Text key={u.id} size="sm">
          {i + 1}. {unitLabel(units, i)}
          {!u.number.trim() && <Text span c="dimmed"> — номер не присвоен</Text>}
        </Text>
      ))}
    </Stack>
  );
}
